import React, { useState, useMemo } from 'react';
import { 
   
  TrendingUp, 
  Coins, 
  Briefcase 
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';

// --- SHARED COMPONENTS ---

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="bg-slate-900 text-white rounded-lg px-4 py-3 shadow-xl text-xs font-mono space-y-1">
      
      {/* Year */}
      <div className="text-slate-300 font-bold mb-1">
        {label}
      </div>

      {payload.map((item: any, idx: number) => {
        let labelName = '';

        if (item.dataKey === 'Invested') labelName = 'Invested Amount';
        else if (item.dataKey === 'Value') labelName = 'Current Value';
        else if (item.dataKey === 'Corpus') labelName = 'Remaining Corpus';
        else if (item.dataKey === 'Withdrawn') labelName = 'Total Withdrawn';
        else if (item.dataKey === 'InitialInvestment') labelName = 'Initial Investment';

        else return null;

        return (
          <div key={idx} className="flex justify-between gap-6">
            <span className="text-slate-400">{labelName}</span>
            <span className="font-bold">
              ₹ {Math.round(item.value).toLocaleString('en-IN')}
            </span>
          </div>
        );
      })}
    </div>
  );
};



// ✅ SINGLE SOURCE OF TRUTH (Indian format)
const formatINRNumber = (value: number) =>
  Math.round(value).toLocaleString('en-IN');



const InputGroup = ({ 
  label, 
  value, 
  setValue, 
  min, 
  max, 
  step = 1, 
  unit = '', 
  prefix = '' 
}: { 
  label: string; 
  value: number; 
  setValue: (val: number) => void; 
  min: number; 
  max: number; 
  step?: number; 
  unit?: string; 
  prefix?: string; 
}) => {
  

  return (
    <div className="mb-6 group">
      <div className="flex justify-between items-center mb-2">
        <label className=" text-base md:text-md font-[450] text-gray-900 tracking-tight uppercase  items-center ">
          {label}
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-bold">{prefix}</span>
        {/*
        <input
  type="text"
  inputMode="numeric"
  value={String(value)}
  onChange={(e) => {
    let raw = e.target.value.replace(/\D/g, '');

    // allow empty → treat as 0
    if (raw === '') {
      setValue(0);
      return;
    }

    // remove leading zeros (01 → 1)
    raw = raw.replace(/^0+(?=\d)/, '');

    const val = Number(raw);
    if (!isNaN(val)) {
      setValue(val);
    }
  }}
  className={`w-32 py-1 pl-8 ${unit ? 'pr-14' : 'pr-4'}
    text-right border border-gray-300 rounded-lg
    text-gray-900 font-mono font-bold
    focus:ring-2 focus:ring-red-500 focus:border-red-500
    outline-none transition-all`}
/>
*/}


 <input
  type="text"
  inputMode="numeric"
 value={formatINRNumber(value)}


  onChange={(e) => {
    let raw = e.target.value.replace(/\D/g, '');

    // allow empty → treat as 0
    if (raw === '') {
      setValue(0);
      return;
    }

    // remove leading zeros (01 → 1)
    raw = raw.replace(/^0+(?=\d)/, '');

    const val = Number(raw);
    if (!isNaN(val)) {
      setValue(val);
    }
  }}
  className={`w-32 py-1 pl-8 ${unit ? 'pr-14' : 'pr-4'}
    text-right border border-gray-300 rounded-lg
    text-gray-900 font-mono font-bold
    focus:ring-2 focus:ring-red-500 focus:border-red-500
    outline-none transition-all`}
/>


          {unit && (
  <span className="absolute right-3 top-1/2 -translate-y-1/2
    text-gray-400 text-xs font-bold pointer-events-none">
    {unit}
  </span>
)}

        </div>
      </div>
      
      <div className="relative h-6 flex items-center">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => setValue(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600 hover:accent-red-700 transition-all"
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400 font-mono mt-1">
        <span>{prefix}{min.toLocaleString()}</span>
        <span>{prefix}{max.toLocaleString()}</span>
      </div>
    </div>
  );
};

// --- MAIN CALCULATOR COMPONENT ---

const Calculator = () => {
  const [activeTab, setActiveTab] = useState<'sip' | 'lumpsum' | 'swp' | 'retirement' | 'marriage' | 'education'>('sip');
 

  // --- STATE MANAGEMENT ---

  // SIP / Step-up State
  const [sipAmount, setSipAmount] = useState(5000);
  const [sipYears, setSipYears] = useState(10);
  const [sipReturn, setSipReturn] = useState(12);
  const [stepUpRate, setStepUpRate] = useState(10); 

  // Lumpsum State
  const [lumpsumAmount, setLumpsumAmount] = useState(100000);
  const [lumpsumYears, setLumpsumYears] = useState(10);
  const [lumpsumReturn, setLumpsumReturn] = useState(12);

  // Retirement State
  const [currentAge, setCurrentAge] = useState(30);
  const [retireAge, setRetireAge] = useState(60);
  const [lifeExpectancy, setLifeExpectancy] = useState(80);
  const [monthlyExpense, setMonthlyExpense] = useState(30000);
  const [inflation, setInflation] = useState(6);
  const [currentSavings, setCurrentSavings] = useState(100000); 
 const [hasExistingSavings, setHasExistingSavings] = useState<'yes' | 'no'>('no');   
  const [preRetireReturn, setPreRetireReturn] = useState(12);
  const [postRetireReturn, setPostRetireReturn] = useState(7);

  // SWP State
const [swpCorpus, setSwpCorpus] = useState(1000000); // initial investment
const [swpWithdrawal, setSwpWithdrawal] = useState(10000); // monthly withdrawal
const [swpYears, setSwpYears] = useState(10);
const [swpReturn, setSwpReturn] = useState(8);
const [swpStartAfter, setSwpStartAfter] = useState(0); // years

// Marriage Calculator State
const [currentMarriageCost, setCurrentMarriageCost] = useState(1000000);
const [marriageInflation, setMarriageInflation] = useState(6);
const [childCurrentAge, setChildCurrentAge] = useState(5);
const [marriageAge, setMarriageAge] = useState(21);
const [marriageReturn, setMarriageReturn] = useState(8);
const [marriageSavings, setMarriageSavings] = useState(0);

// Education Planner State
const [currentEducationCost, setCurrentEducationCost] = useState(3000000);
const [educationInflation, setEducationInflation] = useState(6);
const [educationYears, setEducationYears] = useState(10);
const [educationReturn, setEducationReturn] = useState(12);
const [educationSavings, setEducationSavings] = useState(30000);


  const formatINR = (value: number) =>
  `₹ ${Math.round(value).toLocaleString('en-IN')}`;

const calcMarriagePlan = (
  currentMarriageCost: number,
  marriageInflation: number,
  years: number,
  marriageReturn: number,
  marriageSavings: number
) => {
  if (years <= 0) {
    return {
      futureCost: 0,
      monthlyInvestment: 0,
      monthlyInvestmentRaw: 0, // ✅ ADD THIS
    };
  }

  const futureCost =
    currentMarriageCost * Math.pow(1 + marriageInflation / 100, years);

  const futureValueOfSavings =
    marriageSavings * Math.pow(1 + marriageReturn / 100, years);

  const requiredCorpus = Math.max(0, futureCost - futureValueOfSavings);

  const months = years * 12;
  const monthlyRate = marriageReturn / 12 / 100;
  const factor = Math.pow(1 + monthlyRate, months);

  const monthlyInvestmentRaw =
    monthlyRate === 0
      ? requiredCorpus / months
      : (requiredCorpus * monthlyRate) / ((factor - 1) * (1 + monthlyRate));

  return {
    futureCost: Math.round(futureCost),
    monthlyInvestmentRaw,                 // ✅ RAW
    monthlyInvestment: Math.round(monthlyInvestmentRaw), // ✅ UI
  };
};



const calculateStepUpInvested = (
  baseMonthly: number,
  stepUp: number,
  years: number
) => {
  let invested = 0;
  let currentMonthly = baseMonthly;

  for (let y = 1; y <= years; y++) {
    for (let m = 1; m <= 12; m++) {
      invested += currentMonthly;
    }
    currentMonthly = currentMonthly * (1 + stepUp / 100);
  }

  return Math.round(invested);
};


  // --- CALCULATION LOGIC ---

  const chartData = useMemo(() => {
    let swpTotalWithdrawn = 0;
let swpEndingCorpus = swpCorpus;

    let data = [];
    
if (activeTab === 'sip') {
  let currentValue = 0;
  let currentMonthly = sipAmount;
  const monthlyRate = sipReturn / 12 / 100;

  for (let year = 1; year <= sipYears; year++) {

    for (let m = 1; m <= 12; m++) {
      // Angel One: SIP at month start
      currentValue += currentMonthly;

      // then grow
      currentValue = currentValue * (1 + monthlyRate);
    }

    const investedTillYear = calculateStepUpInvested(
      sipAmount,
      stepUpRate,
      year
    );

    data.push({
      year: `Yr ${year}`,
      Invested: investedTillYear,
      Value: Math.round(currentValue),
    });

    // yearly step-up (Month-13 effect)
    currentMonthly = currentMonthly * (1 + stepUpRate / 100);
  }
}

    else if (activeTab === 'lumpsum') {
      let currentValue = lumpsumAmount;
      const annualRate = lumpsumReturn / 100;

      for (let year = 1; year <= lumpsumYears; year++) {
        currentValue = currentValue * (1 + annualRate);
        data.push({
          year: `Yr ${year}`,
          Invested: lumpsumAmount,
          Value: Math.round(currentValue),
        });
      }
    }

   else if (activeTab === 'swp') {

  let corpus = swpCorpus;
  const monthlyRate = Math.pow(1 + swpReturn / 100, 1 / 12) - 1;

  const swpMonths = swpYears * 12;

  // -----------------------------
  // 1️⃣ Growth Phase (Push to chart)
  // -----------------------------

  // Calculate corpus before SWP
if (swpStartAfter > 0) {
  corpus =
    swpCorpus *
    Math.pow(1 + swpReturn / 100, swpStartAfter);

  data.push({
    year: `Yr ${swpStartAfter}`,
    Corpus: Math.round(corpus),
    Withdrawn: 0,
    InitialInvestment: swpCorpus,
  });
}

  // -----------------------------
  // 2️⃣ SWP Phase
  // -----------------------------

  for (let m = 1; m <= swpMonths; m++) {

    // grow first
    corpus = corpus * (1 + monthlyRate);

    // then withdraw
    corpus = corpus - swpWithdrawal;
    swpTotalWithdrawn += swpWithdrawal;

    swpEndingCorpus = corpus;

    if (m % 12 === 0) {
      data.push({
        year: `Yr ${swpStartAfter + m / 12}`,
        Corpus: Math.max(0, Math.round(corpus)),
        Withdrawn: Math.round(swpTotalWithdrawn),
        InitialInvestment: swpCorpus,
      });
    }

    if (corpus <= 0) break;
  }
}

    
else if (activeTab === 'retirement') {

  let corpus =
    hasExistingSavings === 'yes' ? currentSavings : 1000;

  let expenses = monthlyExpense;
  const expenseInflation = inflation / 100;

  for (let age = currentAge + 1; age <= lifeExpectancy; age++) {

    // Before retirement
    if (age <= retireAge) {
      corpus = corpus * (1 + preRetireReturn / 100);
      expenses = expenses * (1 + expenseInflation);

      data.push({
        year: `Yr ${age}`,
        Corpus: Math.round(corpus),
      });
    }

    // After retirement
    else {
      const annualExpense = expenses * 12;

      if (corpus <= annualExpense) break;

      corpus =
        (corpus - annualExpense) *
        (1 + postRetireReturn / 100);

      expenses = expenses * (1 + expenseInflation);

      data.push({
        year: `Yr ${age}`,
        Corpus: Math.round(Math.max(0, corpus)),
      });

      if (corpus <= 0) break;
    }
  }
}

else if (activeTab === 'marriage') {
  const years = marriageAge - childCurrentAge;

  if (years > 0) {
    const plan = calcMarriagePlan(
      currentMarriageCost,
      marriageInflation,
      years,
      marriageReturn,
      marriageSavings
    );

    const monthlyInvestmentRaw = plan.monthlyInvestmentRaw;

    let projectedCost = currentMarriageCost;
    let investedCash = marriageSavings;

    data.push({
      year: 'Yr 0',
      Value: Math.round(projectedCost),
      Invested: Math.round(investedCash),
    });

    for (let y = 1; y <= years; y++) {
      projectedCost *= 1 + marriageInflation / 100;
     investedCash += Math.round(monthlyInvestmentRaw) * 12;


      data.push({
        year: `Yr ${y}`,
        Value: Math.round(projectedCost),
        Invested: Math.round(investedCash),
      });
    }
  }
}




else if (activeTab === 'education') {
  const years = educationYears;

  const futureCost =
    currentEducationCost *
    Math.pow(1 + educationInflation / 100, years);

  const futureSavings =
    educationSavings *
    Math.pow(1 + educationReturn / 100, years);

  const requiredCorpus = Math.max(
    0,
    futureCost - futureSavings
  );

  const months = years * 12;
  const monthlyRate = educationReturn / 12 / 100;
  const factor = Math.pow(1 + monthlyRate, months);

  const monthlyInvestment =
    monthlyRate === 0
      ? requiredCorpus / months
      : (requiredCorpus * monthlyRate) /
        ((factor - 1) * (1 + monthlyRate));

  let projectedCost = currentEducationCost;
  let invested = educationSavings;

  // Yr 0
  data.push({
    year: 'Yr 0',
    Value: Math.round(projectedCost),
    Invested: Math.round(invested),
  });

  for (let y = 1; y <= years; y++) {
    projectedCost *= 1 + educationInflation / 100;
    invested += Math.round(monthlyInvestment) * 12;

    data.push({
      year: `Yr ${y}`,
      Value: Math.round(projectedCost),
      Invested: Math.round(invested),
    });
  }
}



    // ✅ attach SWP final values (NOT from chart points)
(data as any).swpMeta = {
  withdrawn: Math.round(swpTotalWithdrawn),
  endingCorpus: Math.round(swpEndingCorpus),
};

return data;

  }, [
  activeTab,

  // SIP
  sipAmount,
  sipYears,
  sipReturn,
  stepUpRate,

  // LUMPSUM
  lumpsumAmount,
  lumpsumYears,
  lumpsumReturn,

  // SWP ✅ (THIS IS THE FIX)
  swpCorpus,
  swpWithdrawal,
  swpYears,
  swpReturn,
  swpStartAfter,

  // RETIREMENT
  currentAge,
  retireAge,
  lifeExpectancy,
  monthlyExpense,
  inflation,
  currentSavings,
  //monthlySavings,
  preRetireReturn,
  postRetireReturn,
 hasExistingSavings,

 // EDUCATION ✅
  currentEducationCost,
  educationInflation,
  educationYears,
  educationReturn,
  educationSavings,

  // MARRIAGE ✅
  currentMarriageCost,
  childCurrentAge,
  marriageAge,
  marriageInflation,
  marriageReturn,
  marriageSavings,

]);


const marriageSummary = useMemo(() => {
  const years = marriageAge - childCurrentAge;

  return calcMarriagePlan(
    currentMarriageCost,
    marriageInflation,
    years,
    marriageReturn,
    marriageSavings
  );
}, [
  currentMarriageCost,
  marriageInflation,
  childCurrentAge,
  marriageAge,
  marriageReturn,
  marriageSavings,
]);



const educationSummary = useMemo(() => {
  const years = educationYears;

  if (years <= 0) {
    return { futureCost: 0, monthlyInvestment: 0 };
  }

  // 1️⃣ Inflate cost
  const futureCost = Math.round(
    currentEducationCost *
    Math.pow(1 + educationInflation / 100, years)
  );

  // 2️⃣ Grow savings
  const futureSavings = Math.round(
    educationSavings *
    Math.pow(1 + educationReturn / 100, years)
  );

  // 3️⃣ Net corpus
  const requiredCorpus = Math.max(
    0,
    futureCost - futureSavings
  );

  // 4️⃣ SIP (BEGINNING OF MONTH — CRITICAL)
  const months = years * 12;
  const monthlyRate = educationReturn / 12 / 100;
  const factor = Math.pow(1 + monthlyRate, months);

  const monthlyInvestment =
    monthlyRate === 0
      ? requiredCorpus / months
      : (requiredCorpus * monthlyRate) /
        ((factor - 1) * (1 + monthlyRate));

  return {
    futureCost,
    monthlyInvestment: Math.round(monthlyInvestment),
  };
}, [
  currentEducationCost,
  educationInflation,
  educationYears,
  educationReturn,
  educationSavings,
]);




  // --- SUMMARY CALCULATIONS ---
  const summary = useMemo(() => {
  if (chartData.length === 0) {
    return { invested: 0, total: 0, profit: 0 };
  }

  const lastPoint = chartData[chartData.length - 1] as any;

  // --- SWP SUMMARY ---
 if (activeTab === 'swp') {
  const meta = (chartData as any).swpMeta || {
    withdrawn: 0,
    endingCorpus: 0,
  };

  return {
    invested: swpCorpus,
    withdrawn: meta.withdrawn,
    total: meta.endingCorpus, // ✅ Final Balance Amount (correct)
    profit: meta.withdrawn + meta.endingCorpus - swpCorpus,
  };
}




  // --- RETIREMENT SUMMARY ---
 if (activeTab === 'retirement') {
  const yearsToRetirement = retireAge - currentAge;
  const retirementYears = lifeExpectancy - retireAge;

  // 1️⃣ Monthly expense at retirement (inflation adjusted)
  const monthlyExpenseAtRetirement =
    monthlyExpense * Math.pow(1 + inflation / 100, yearsToRetirement);

  // 2️⃣ Annual expense at retirement
  const annualExpenseAtRetirement =
    monthlyExpenseAtRetirement * 12;

  // 3️⃣ Angel One corpus calculation (PV of annuity)
// 3️⃣ Angel One corpus calculation (PV of GROWING annuity)
// 3️⃣ Angel One corpus calculation (REAL RETURN METHOD)
const r = postRetireReturn / 100;
const g = inflation / 100;
const n = retirementYears;

// Angel One uses REAL return
const realReturn = (1 + r) / (1 + g) - 1;

// Annuity factor using real return
const annuityFactor =
  (1 - Math.pow(1 + realReturn, -n)) / realReturn;

const requiredCorpus =
  annualExpenseAtRetirement * annuityFactor;



  // 4️⃣ Existing savings grown till retirement
  const existingCorpus =
    hasExistingSavings === 'yes'
      ? currentSavings *
        Math.pow(1 + preRetireReturn / 100, yearsToRetirement)
      : 0;

  // 5️⃣ Additional corpus required
  const additionalCorpusRequired = Math.max(
    0,
    requiredCorpus - existingCorpus
  );

  console.log({
  annualExpenseAtRetirement,
  annuityFactor,
  requiredCorpus,
});

  // 6️⃣ Monthly SIP required
  const monthlyRate = preRetireReturn / 12 / 100;
  const months = yearsToRetirement * 12;

  const monthlySavingsRequired =
    monthlyRate === 0
      ? additionalCorpusRequired / months
      : (additionalCorpusRequired * monthlyRate) /
        (Math.pow(1 + monthlyRate, months) - 1);

  return {
    annualIncomeAtRetirement: Math.round(annualExpenseAtRetirement),
    additionalCorpusRequired: Math.round(additionalCorpusRequired),
    monthlySavingsRequired: Math.round(monthlySavingsRequired),
    invested: 0,
    profit: 0,
  };
}


  // --- SIP / LUMPSUM ---
  const val = lastPoint.Value || 0;
  const inv =
    activeTab === 'sip'
      ? calculateStepUpInvested(sipAmount, stepUpRate, sipYears)
      : lastPoint.Invested || 0;

  return {
    invested: inv,
    total: val,
    profit: val - inv,
  };
}, [
  chartData,
  activeTab,
  sipAmount,
  stepUpRate,
  sipYears,
  swpCorpus,
  retireAge,
  currentAge,
  inflation,
  monthlyExpense,
  currentSavings,
  hasExistingSavings,
  preRetireReturn,
 postRetireReturn,
 lifeExpectancy,
]);


  return (
    // UPDATED: Changed pt-10 to pt-36 (This adds approx 144px space at the top)
    // This should definitely clear your navigation bar.
    <div className="min-h-screen bg-gray-50 text-gray-800 font-sans pb-20 pt-36">
      
      <div className="max-w-7xl mx-auto px-4">
        
        {/* "Back to Home" button REMOVED completely as requested */}

        {/* --- TABS --- */}
       <div className="flex justify-center mb-10">
  {/* Main wrapper remains white with border as per your screenshot */}
  <div className="bg-white p-1 rounded-full shadow-sm border border-gray-200 inline-flex flex-wrap justify-center">
    {[
      { id: 'sip', label: 'SIP / Step Up', icon: TrendingUp },
      { id: 'lumpsum', label: 'One-Time', icon: Coins },
      { id: 'swp', label: 'SWP', icon: Coins },
      { id: 'retirement', label: 'Retirement', icon: Briefcase },
      { id: 'marriage', label: 'Marriage', icon: Briefcase },
      { id: 'education', label: 'Education', icon: Briefcase }

    ].map((tab) => (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id as any)}
        // FORCING THE COLOR HERE
        style={{ 
          backgroundColor: activeTab === tab.id ? '#434242' : 'transparent' 
        }}
        className={`flex items-center px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wide transition-all ${
          activeTab === tab.id 
          ? 'text-white shadow-lg' 
          : 'text-gray-500 hover:text-red-600 hover:bg-gray-50'
        }`}
      >
        <tab.icon size={16} className="mr-2" /> {tab.label}
      </button>
    ))}
  </div>
</div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* --- LEFT: CONTROLS --- */}
          <div className="lg:col-span-5 bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100">
            
            {activeTab === 'sip' && (
              <>
                <InputGroup label="Monthly Investment" value={sipAmount} setValue={setSipAmount} min={500} max={100000} step={500} prefix="₹" />
                <InputGroup label="Expected Return (p.a)" value={sipReturn} setValue={setSipReturn} min={5} max={30} step={0.5} unit="%" />
                <InputGroup label="Time Period" value={sipYears} setValue={setSipYears} min={1} max={40} step={1} unit=" Yr" />
                
                <div className="p-4 bg-red-50 rounded-lg border border-red-100 mt-6">
                  <div className="flex items-center gap-2 mb-4 text-red-800 font-bold text-sm uppercase">
                    <TrendingUp size={16}/> Step-Up Strategy
                  </div>
                  <InputGroup label="Yearly Increment" value={stepUpRate} setValue={setStepUpRate} min={0} max={50} step={1} unit="%" />
                  <p className="text-xs text-red-600/80 mt-2">
                    *Increasing your SIP by {stepUpRate}% every year helps beat inflation.
                  </p>
                </div>
              </>
            )}

            {activeTab === 'lumpsum' && (
              <>
                <InputGroup label="Total Investment" value={lumpsumAmount} setValue={setLumpsumAmount} min={5000} max={10000000} step={5000} prefix="₹" />
                <InputGroup label="Expected Return (p.a)" value={lumpsumReturn} setValue={setLumpsumReturn} min={5} max={30} step={0.5} unit="%" />
                <InputGroup label="Time Period" value={lumpsumYears} setValue={setLumpsumYears} min={1} max={40} step={1} unit=" Yr" />
              </>
            )}

            {activeTab === 'swp' && (
  <>
    <InputGroup
      label="Initial Investment"
      value={swpCorpus}
      setValue={setSwpCorpus}
      min={100000}
      max={50000000}
      step={50000}
      prefix="₹"
    />

    <InputGroup
      label="Monthly Withdrawal"
      value={swpWithdrawal}
      setValue={setSwpWithdrawal}
      min={1000}
      max={500000}
      step={1000}
      prefix="₹"
    />

    <InputGroup
      label="Expected Return (p.a)"
      value={swpReturn}
      setValue={setSwpReturn}
      min={4}
      max={15}
      step={0.5}
      unit="%"
    />

    <InputGroup
      label="Time Period"
      value={swpYears}
      setValue={setSwpYears}
      min={1}
      max={40}
      step={1}
      unit=" Yr"
    />

    <InputGroup
      label="SWP Starts After"
      value={swpStartAfter}
      setValue={setSwpStartAfter}
      min={0}
      max={10}
      step={1}
      unit=" Yr"
    />
  </>
)}

{activeTab === 'marriage' && (
  <>
    <InputGroup
      label="Current Marriage Expense"
      value={currentMarriageCost}
      setValue={setCurrentMarriageCost}
      min={100000}
      max={10000000}
      step={50000}
      prefix="₹"
    />

    <InputGroup
      label="Expected Inflation (p.a)"
      value={marriageInflation}
      setValue={setMarriageInflation}
      min={2}
      max={15}
      unit="%"
    />

    <InputGroup
      label="Child's Current Age"
      value={childCurrentAge}
      setValue={setChildCurrentAge}
      min={0}
      max={18}
      unit=" Yrs"
    />

    <InputGroup
      label="Marriage Age"
      value={marriageAge}
      setValue={setMarriageAge}
      min={childCurrentAge + 1}
      max={30}
      unit=" Yrs"
    />

    <InputGroup
      label="Expected Return on Investment"
      value={marriageReturn}
      setValue={setMarriageReturn}
      min={4}
      max={15}
      unit="%"
    />

    <InputGroup
      label="Existing Savings"
      value={marriageSavings}
      setValue={setMarriageSavings}
      min={0}
      max={5000000}
      step={50000}
      prefix="₹"
    />
  </>
)}


{activeTab === 'education' && (
  <>
    <InputGroup
      label="Current Cost of Education"
      value={currentEducationCost}
      setValue={setCurrentEducationCost}
      min={100000}
      max={10000000}
      step={50000}
      prefix="₹"
    />

    <InputGroup
      label="Expected Inflation (p.a)"
      value={educationInflation}
      setValue={setEducationInflation}
      min={2}
      max={15}
      unit="%"
    />

    <InputGroup
      label="Years Until Education"
      value={educationYears}
      setValue={setEducationYears}
      min={1}
      max={30}
      unit=" Yrs"
    />

    <InputGroup
      label="Expected Return on Investment"
      value={educationReturn}
      setValue={setEducationReturn}
      min={5}
      max={20}
      unit="%"
    />

    <InputGroup
      label="Existing Savings"
      value={educationSavings}
      setValue={setEducationSavings}
      min={0}
      max={5000000}
      step={50000}
      prefix="₹"
    />
  </>
)}


            {activeTab === 'retirement' && (
              <div className="space-y-6 h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                 <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                    <h3 className="text-blue-800 font-bold text-xs uppercase mb-4">Personal Details</h3>
                    <InputGroup label="Current Age" value={currentAge} setValue={setCurrentAge} min={18} max={70} unit=" Yrs" />
                    <InputGroup label="Retirement Age" value={retireAge} setValue={setRetireAge} min={currentAge + 1} max={80} unit=" Yrs" />
                    <InputGroup label="Life Expectancy" value={lifeExpectancy} setValue={setLifeExpectancy} min={retireAge + 1} max={100} unit=" Yrs" />
                    <div className="mb-6">
  <label className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 block">
    Existing Savings
  </label>
  <div className="flex gap-6 items-center">
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        checked={hasExistingSavings === 'yes'}
        onChange={() => setHasExistingSavings('yes')}
        className="accent-red-600"
      />
      <span className="text-sm font-medium">Yes</span>
    </label>
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        checked={hasExistingSavings === 'no'}
        onChange={() => setHasExistingSavings('no')}
        className="accent-red-600"
      />
      <span className="text-sm font-medium">No</span>
    </label>
  </div>
</div>
{hasExistingSavings === 'yes' && (
  <InputGroup
    label="Existing Retirement Fund"
    value={currentSavings}
    setValue={setCurrentSavings}
    min={0}
    max={50000000}
    step={10000}
    prefix="₹"
  />
)}

                   </div>

                 <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                    <h3 className="text-red-800 font-bold text-xs uppercase mb-4">Finances</h3>
                    <InputGroup label="Monthly Expenses" value={monthlyExpense} setValue={setMonthlyExpense} min={5000} max={500000} prefix="₹" />
                    <InputGroup label="Rate of Inflation" value={inflation} setValue={setInflation} min={2} max={15} unit="%" />
                 </div>

                 <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                    <h3 className="text-green-800 font-bold text-xs uppercase mb-4">Savings & Growth</h3>
                    <InputGroup label="Expected Return of Investment (Before retirement)" value={preRetireReturn} setValue={setPreRetireReturn} min={5} max={20} unit="%" />
                    <InputGroup label="Expected Return of Investment (After retirement)" value={postRetireReturn} setValue={setPostRetireReturn} min={4} max={12} unit="%" />
                 </div>
              </div>
            )}
            
          </div>


          {/* --- RIGHT: GRAPH & RESULTS --- */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            
           {/* Summary Cards */}
{/* Summary Cards */}
{/* SUMMARY CARDS */}

<div className="grid grid-cols-1 md:grid-cols-3 gap-4">

  {/* ---------------- SIP / LUMPSUM / SWP ---------------- */}
  {activeTab !== 'retirement' && activeTab !== 'marriage' && activeTab !== 'education' && (
    <>
      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="text-gray-500 text-xs font-bold uppercase mb-1">
          {activeTab === 'swp' ? 'Initial Investment' : 'Invested Amount'}
        </div>
        <div className="text-2xl font-bold text-gray-900">
          {formatINR(summary.invested)}
        </div>
      </div>

      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="text-green-500 text-xs font-bold uppercase mb-1">
          {activeTab === 'swp' ? 'Total Withdrawn' : 'Est. Returns'}
        </div>
        <div className="text-2xl font-bold text-green-600">
          {activeTab === 'swp'
            ? formatINR((summary as any).withdrawn || 0)
            : formatINR(summary.profit)}
        </div>
      </div>

      <div
        style={{ backgroundColor: '#434242' }}
        className="p-5 rounded-xl text-white"
      >
        <div className="text-xs font-bold uppercase mb-1">
          {activeTab === 'swp' ? 'Remaining Corpus' : 'Total Value'}
        </div>
        <div className="text-2xl font-bold">
          {formatINR(summary.total)}
        </div>
      </div>
    </>
  )}

  {/* ---------------- RETIREMENT ---------------- */}
  {activeTab === 'retirement' && (
    <>
      <div className="bg-white p-5 rounded-xl border shadow-sm">
        <div className="text-gray-500 text-xs font-bold uppercase mb-1">
          Annual Income Required at {retireAge}
        </div>
        <div className="text-2xl font-bold">
         {/* ₹ {(summary as any).annualIncomeAtRetirement.toLocaleString('en-IN')} */}
         {formatINR((summary as any).annualIncomeAtRetirement || 0)}

        </div>
      </div>

      <div className="bg-white p-5 rounded-xl border shadow-sm">
        <div className="text-green-500 text-xs font-bold uppercase mb-1">
          Additional Income Needed
        </div>
        <div className="text-2xl font-bold text-green-600">
         {/* ₹ {(summary as any).additionalCorpusRequired.toLocaleString('en-IN')} */}
          {formatINR((summary as any).additionalCorpusRequired || 0)}

        </div>
      </div>

      <div
        style={{ backgroundColor: '#434242' }}
        className="p-5 rounded-xl text-white"
      >
        <div className="text-xs font-bold uppercase mb-1">
          Monthly Savings Required
        </div>
        <div className="text-2xl font-bold">
         {/*  ₹ {(summary as any).monthlySavingsRequired.toLocaleString('en-IN')} */}
          {formatINR((summary as any).monthlySavingsRequired || 0)}

        </div>
      </div>
    </>
  )}

  {/* ---------------- MARRIAGE ---------------- */}
{/* ---------------- MARRIAGE ---------------- */}
{activeTab === 'marriage' && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-3">
    <div className="bg-white p-5 rounded-xl border shadow-sm">
      <div className="text-gray-500 text-xs font-bold uppercase mb-1">
        Future Cost of Marriage
      </div>
      <div className="text-2xl font-bold text-gray-900">
        ₹ {marriageSummary.futureCost.toLocaleString('en-IN')}
      </div>
    </div>

    <div
      style={{ backgroundColor: '#434242' }}
      className="p-5 rounded-xl text-white"
    >
      <div className="text-xs font-bold uppercase mb-1">
        Monthly Investment Required
      </div>
      <div className="text-2xl font-bold">
        ₹ {marriageSummary.monthlyInvestment.toLocaleString('en-IN')}
      </div>
    </div>
  </div>
)}


{activeTab === 'education' && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:col-span-3">
    <div className="bg-white p-5 rounded-xl border shadow-sm">
      <div className="text-gray-500 text-xs font-bold uppercase mb-1">
        Future Cost of Education
      </div>
      <div className="text-2xl font-bold">
        ₹ {educationSummary.futureCost.toLocaleString('en-IN')}
      </div>
    </div>

    <div
      style={{ backgroundColor: '#434242' }}
      className="p-5 rounded-xl text-white"
    >
      <div className="text-xs font-bold uppercase mb-1">
        Monthly Investment Required
      </div>
      <div className="text-2xl font-bold">
        ₹ {educationSummary.monthlyInvestment.toLocaleString('en-IN')}
      </div>
    </div>
  </div>
)}


</div>



            {/* Main Graph */}
            <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100 h-[500px] flex flex-col">
              <div className="flex justify-between items-center mb-6">
                 <h3 className="text-gray-900 text-base md:text-md tracking-tight  uppercase  flex items-center gap-2">
                   <TrendingUp size={16} className="text-red-600"/> Wealth Projection
                 </h3>
                 <div className="flex gap-4 text-xs font-bold">
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded-full"></div>  {activeTab === 'swp' || activeTab === 'retirement'
      ? 'Remaining Corpus'
      : activeTab === 'marriage' || activeTab === 'education'
      ? 'Goal Requirement'
      : 'Wealth'}</div>
                    {activeTab !== 'retirement' && activeTab !== 'swp' && <div className="flex items-center gap-1"><div className="w-3 h-3 bg-gray-300 rounded-full"></div> Invested</div>}
                 </div>
              </div>

              <div className="flex-grow">
                <ResponsiveContainer width="100%" height="100%">
                 <AreaChart
  data={chartData}
  margin={{ top: 20, right: 10, left: 0, bottom: 40 }}
>

                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#94a3b8" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                   <XAxis
  dataKey="year"
  tickFormatter={(value) => value.replace('Yr ', '')}
  tick={{ fontSize: 12, fontFamily: 'monospace' }}
  stroke="#9ca3af"
  tickLine={false}
  axisLine={false}
  label={{
    value: 'Years',
    position: 'insideBottom',
    offset: -12,   // 👈 important
    style: {
      fill: '#6b7280',
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'monospace',
    },
  }}
/>


                    <YAxis
  tickFormatter={(val) => (val / 100000).toFixed(1)}
  tick={{ fontSize: 12, fontFamily: 'monospace' }}
  stroke="#9ca3af"
  tickLine={false}
  axisLine={false}
  label={{
    value: 'Amount in Lakhs',
    angle: -90,
     position: 'center',   // ✅ THIS CENTERS IT
    dx: -22,               // ✅ move left away from ticks
    offset: 10,
    
    style: {
      fill: '#6b7280',
      fontSize: 14,
      fontWeight: 600,
      fontFamily: 'monospace',
    },
  }}
/>

                    <Tooltip content={<CustomTooltip />} />

                    
                   {activeTab === 'swp' ? (
  <>
    {/* Visible area */}
    <Area
      type="monotone"
      dataKey="Corpus"
      stroke="#ef4444"
      strokeWidth={3}
      fillOpacity={1}
      fill="url(#colorValue)"
    />

    {/* Invisible area ONLY for tooltip */}
    <Area
      type="monotone"
      dataKey="InitialInvestment"
      stroke="transparent"
      fill="transparent"
      isAnimationActive={false}
      activeDot={false}
    />
  </>
) : activeTab !== 'retirement' ? (
  <>
    <Area
      type="monotone"
      dataKey="Value"
      stroke="#ef4444"
      strokeWidth={3}
      fillOpacity={1}
      fill="url(#colorValue)"
    />
    <Area
      type="monotone"
      dataKey="Invested"
      stroke="#94a3b8"
      strokeWidth={2}
      strokeDasharray="5 5"
      fillOpacity={1}
      fill="url(#colorInvested)"
    />
  </>
) : (

                      <Area type="monotone" dataKey="Corpus" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;